import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Button, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TextInput } from "../../TextInput";
import { Select } from "../../Select";
import { useModal } from "../ModalProvider";
import { UnsavedChangesModalContent } from "../UnsavedChanges";
import {
  cdtCodesService,
  CDTCode as APICDTCode,
} from "../../../services/cdtCodesService";
import {
  ContentContainer,
  ModalTitle,
  FieldsContainer,
  RowFieldsContainer,
  ButtonsContainer,
} from "./AddTreatmentStepModal.styled";

export interface CDTCode {
  code: string;
  description: string;
  laymanExplanation: string;
  category: string;
  defaultFee: number;
  defaultDurationMinutes: number;
}

export interface TreatmentStepData {
  cdtCodes: CDTCode[];
  explanation: string;
  duration: string;
  feeAmount: string;
  timeUntilNext: number;
  timeUnit: "days" | "weeks" | "months";
}

interface AddTreatmentStepModalProps {
  onSave: (data: TreatmentStepData) => void;
  onCancel: () => void;
}

const TIME_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1),
}));

const UNIT_OPTIONS = [
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
];

const AddTreatmentStepModalContent: React.FC<AddTreatmentStepModalProps> = ({
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();

  // CDT codes state
  const [cdtCodes, setCdtCodes] = useState<CDTCode[]>([]);
  const [cdtLoading, setCdtLoading] = useState(false);

  // Form state
  const [selectedCodeValues, setSelectedCodeValues] = useState<string[]>([]);
  const [explanation, setExplanation] = useState("");
  const [duration, setDuration] = useState("");
  const [feeAmount, setFeeAmount] = useState("0");
  const [timeUntilNext, setTimeUntilNext] = useState<number>(2);
  const [timeUnit, setTimeUnit] = useState<"days" | "weeks" | "months">(
    "weeks",
  );

  // Error state
  const [errors, setErrors] = useState<{
    cdtCodes?: string;
    explanation?: string;
  }>({});

  // Load CDT codes on mount
  useEffect(() => {
    const fetchCdtCodes = async () => {
      setCdtLoading(true);
      try {
        const codes = await cdtCodesService.getCDTCodes({ activeOnly: true });
        setCdtCodes(
          codes.map((code: APICDTCode) => ({
            code: code.code,
            description: code.description,
            laymanExplanation: code.laymanExplanation,
            category: code.category,
            defaultFee: code.defaultFee,
            defaultDurationMinutes: code.defaultDurationMinutes,
          })),
        );
      } catch (error) {
        console.error("Failed to fetch CDT codes:", error);
      } finally {
        setCdtLoading(false);
      }
    };

    fetchCdtCodes();
  }, []);

  // Check if there are any changes
  const hasChanges = useMemo(() => {
    return (
      selectedCodeValues.length > 0 ||
      explanation.trim() !== "" ||
      duration !== "" ||
      (feeAmount !== "" && feeAmount !== "0")
    );
  }, [selectedCodeValues, explanation, duration, feeAmount]);

  // Convert CDT codes to Select options
  const cdtSelectOptions = useMemo(
    () =>
      cdtCodes.map((code) => ({
        value: code.code,
        label: `${code.code} - ${code.description}`,
        chipLabel: code.code, // Show only code in chips
      })),
    [cdtCodes],
  );

  // Get selected CDT code objects
  const selectedCodes = useMemo(
    () => cdtCodes.filter((code) => selectedCodeValues.includes(code.code)),
    [cdtCodes, selectedCodeValues],
  );

  const handleCodeChange = useCallback(
    (value: string | number | (string | number)[]) => {
      const newValues = Array.isArray(value)
        ? (value as string[])
        : [value as string];
      setSelectedCodeValues(newValues);

      // Get selected code objects
      const newSelectedCodes = cdtCodes.filter((code) =>
        newValues.includes(code.code),
      );

      // Update explanation with combined layman explanations
      const combinedExplanation = newSelectedCodes
        .map((c) => c.laymanExplanation)
        .join(" ");
      setExplanation(combinedExplanation);

      // Update duration (sum of all default durations)
      const totalDuration = newSelectedCodes.reduce(
        (sum, c) => sum + (c.defaultDurationMinutes || 0),
        0,
      );
      setDuration(totalDuration > 0 ? String(totalDuration) : "");

      // Update fee amount (sum of all default fees)
      const totalFee = newSelectedCodes.reduce(
        (sum, c) => sum + (c.defaultFee || 0),
        0,
      );
      setFeeAmount(String(totalFee));

      // Clear error
      if (newValues.length > 0) {
        setErrors((prev) => ({ ...prev, cdtCodes: undefined }));
      }
    },
    [cdtCodes],
  );

  const handleExplanationChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    setExplanation(value);

    // Clear error if valid
    if (value.trim().length > 0 && value.length <= 1000) {
      setErrors((prev) => ({ ...prev, explanation: undefined }));
    }
  };

  const handleDurationChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setDuration(value);
  };

  const handleFeeAmountChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    // Allow digits and one decimal point for cents
    let value = e.target.value
      .replace(/[^0-9.]/g, "")
      .replace(/(\..*)\./g, "$1");
    // Remove leading zeros (but keep "0" for decimals like "0.5")
    if (value.length > 1 && value.startsWith("0") && value[1] !== ".") {
      value = value.replace(/^0+/, "") || "0";
    }
    setFeeAmount(value);
  };

  const handleFeeAmountBlur = () => {
    // If fee amount is empty, set to "0"
    if (!feeAmount.trim()) {
      setFeeAmount("0");
    }
  };

  const validate = (): boolean => {
    const newErrors: { cdtCodes?: string; explanation?: string } = {};

    if (selectedCodeValues.length === 0) {
      newErrors.cdtCodes = t(
        "addTreatmentStep.errors.cdtRequired",
        "Please select at least one CDT code.",
      );
    }

    if (!explanation.trim()) {
      newErrors.explanation = t(
        "addTreatmentStep.errors.explanationRequired",
        "Please enter an explanation for the treatment step.",
      );
    } else if (explanation.length > 1000) {
      newErrors.explanation = t(
        "addTreatmentStep.errors.explanationTooLong",
        "Explanation can not be longer than 1000 characters.",
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddStep = () => {
    if (!validate()) return;

    onSave({
      cdtCodes: selectedCodes,
      explanation,
      duration,
      feeAmount: feeAmount || "0",
      timeUntilNext,
      timeUnit,
    });
  };

  const handleCancel = () => {
    if (hasChanges) {
      // Show unsaved changes modal
      openModal({
        id: "unsaved-changes-modal",
        title: "",
        content: (
          <UnsavedChangesModalContent
            onDiscard={() => {
              closeModal("unsaved-changes-modal");
              onCancel();
            }}
            onBack={() => {
              closeModal("unsaved-changes-modal");
            }}
          />
        ),
        showCloseButton: false,
        maxWidth: "600px",
        containerPadding: "60px 90px",
        contentPadding: "0",
      });
    } else {
      onCancel();
    }
  };

  return (
    <ContentContainer>
      <ModalTitle>
        {t("addTreatmentStep.title", "Add Treatment Step")}
      </ModalTitle>

      <FieldsContainer>
        {/* CDT Code Multi-Select with Search */}
        <Select
          label={t("addTreatmentStep.cdtCodeLabel", "Search CDT Code or Name")}
          placeholder={t(
            "addTreatmentStep.cdtCodePlaceholder",
            "Search and select CDT codes...",
          )}
          options={cdtSelectOptions}
          onChange={handleCodeChange}
          value={selectedCodeValues}
          fullWidth
          required
          multiple
          searchable
          showChips
          searchPlaceholder={t(
            "addTreatmentStep.searchPlaceholder",
            "Search...",
          )}
          loading={cdtLoading}
          error={!!errors.cdtCodes}
          helperText={errors.cdtCodes}
        />

        {/* Explanation - 100px height */}
        <TextInput
          label={t("addTreatmentStep.explanationLabel", "Explanation:")}
          placeholder={t(
            "addTreatmentStep.explanationPlaceholder",
            "Enter explanation...",
          )}
          value={explanation}
          onChange={handleExplanationChange}
          multiline
          rows={4}
          fullWidth
          required
          error={!!errors.explanation}
          helperText={errors.explanation}
          height="100px"
        />

        {/* Row 1: Duration, Fee Amount */}
        <RowFieldsContainer>
          <Box sx={{ flex: 1 }}>
            <TextInput
              label={t("addTreatmentStep.durationLabel", "Duration, min")}
              placeholder=""
              value={duration}
              onChange={handleDurationChange}
              fullWidth
              type="text"
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <TextInput
              label={t("addTreatmentStep.feeAmountLabel", "Fee Amount, $")}
              placeholder=""
              value={feeAmount}
              onChange={handleFeeAmountChange}
              onBlur={handleFeeAmountBlur}
              fullWidth
              type="text"
              required
            />
          </Box>
        </RowFieldsContainer>

        {/* Row 2: Time Until Next, Units */}
        <RowFieldsContainer>
          <Box sx={{ flex: 1 }}>
            <Select
              label={t(
                "addTreatmentStep.timeUntilNextLabel",
                "Time until next treatment step",
              )}
              value={timeUntilNext}
              onChange={(value) => setTimeUntilNext(value as number)}
              options={TIME_OPTIONS}
              fullWidth
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Select
              label={t("addTreatmentStep.unitsLabel", "Units")}
              value={timeUnit}
              onChange={(value) =>
                setTimeUnit(value as "days" | "weeks" | "months")
              }
              options={UNIT_OPTIONS}
              fullWidth
            />
          </Box>
        </RowFieldsContainer>
      </FieldsContainer>

      <ButtonsContainer>
        <Button variant="outlined" onClick={handleCancel}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button variant="contained" onClick={handleAddStep}>
          {t("addTreatmentStep.addStepButton", "Add Step")}
        </Button>
      </ButtonsContainer>
    </ContentContainer>
  );
};

export default AddTreatmentStepModalContent;
